import IncidentStatusSummaryFormComponents from 'components/IAP/planningView/widgets/IncidentStatusSummary/IncidentStatusSummaryFormComponents';
import { FormProvider, useForm } from 'react-hook-form';
import {
  IncidentStatusSummaryDto,
  IncidentStatusSummaryFormFields,
  PerAgencyResources,
} from 'types/iapBaseTypes';

type IncidentStatusSummaryItemProps = {
  statusSummary: IncidentStatusSummaryDto | null;
  readOnly?: boolean;
};

const mapAssignedResourcesToFormFields = (
  assignedResources: IncidentStatusSummaryDto['assignedResources'] | undefined
) => {
  if (!assignedResources) return [];

  const resourcesByAgency: Map<string, PerAgencyResources> = new Map();
  const equipmentTypesSet: Set<string> = new Set();

  assignedResources.forEach((resource) => {
    const { agency, type, amount } = resource;
    let currentMappedResource = resourcesByAgency.get(agency);
    if (!currentMappedResource) {
      currentMappedResource = {
        agency,
        personnelCount: '0',
        equipment: [],
      };
    }

    if (type.includes('PERSONNEL')) {
      const currentCount = parseInt(currentMappedResource.personnelCount, 10);
      resourcesByAgency.set(agency, {
        ...currentMappedResource,
        personnelCount: (currentCount + parseInt(amount, 10)).toString(),
      });
    } else {
      equipmentTypesSet.add(type);
      resourcesByAgency.set(agency, {
        ...currentMappedResource,
        equipment: [
          ...currentMappedResource.equipment,
          { type, quantity: amount },
        ],
      });
    }
  });

  return Array.from(resourcesByAgency.values()).map((resource) => {
    // Ensure all equipment types are represented for each agency
    equipmentTypesSet.forEach((eqType) => {
      if (!resource.equipment.find((eq) => eq.type === eqType)) {
        resource.equipment.push({ type: eqType, quantity: '0' });
      }
    });

    resource.equipment.sort((a, b) => a.type.localeCompare(b.type));
    return resource;
  });
};

const getDefaultValues = (
  incidentStatusSummary: IncidentStatusSummaryDto | null
): IncidentStatusSummaryFormFields => ({
  situationSummary: incidentStatusSummary?.situationSummary || '',
  commandPriorities:
    incidentStatusSummary?.commandPriorities.map((cp) => ({
      commandPriority: cp,
    })) || [],
  reportVersion: incidentStatusSummary?.reportVersion || '',
  incidentType: incidentStatusSummary?.incidentType || [],
  notableItems: incidentStatusSummary?.notableItems || [],
  responderInjuries: {
    responderInjuriesThisReportingPeriod:
      incidentStatusSummary?.responderInjuriesThisReportingPeriod || 0,
    responderInjuriesTotal: incidentStatusSummary?.responderInjuriesTotal || 0,
  },
  responderDeaths: {
    responderDeathsThisReportingPeriod:
      incidentStatusSummary?.responderDeathsThisReportingPeriod || 0,
    responderDeathsTotal: incidentStatusSummary?.responderDeathsTotal || 0,
  },
  lifeSafetyHealthIssues: incidentStatusSummary?.lifeSafetyHealthIssues || '',
  marineTransportationSystem:
    incidentStatusSummary?.marineTransportationSystem || '',
  criticalResourceRequests:
    incidentStatusSummary?.criticalResourceRequests.map((cr) => ({
      criticalResourceRequest: cr,
    })) || [],
  plannedActions: incidentStatusSummary?.plannedActions || [],
  assignedResources: mapAssignedResourcesToFormFields(
    incidentStatusSummary?.assignedResources
  ),
  amplifyingInformation: incidentStatusSummary?.amplifyingInformation || '',
  comments: incidentStatusSummary?.comments || '',
  mediaPhotosPublicOutreach:
    incidentStatusSummary?.mediaPhotosPublicOutreach || '',
  otherIncidentType: incidentStatusSummary?.otherIncidentType || '',
});

const IncidentStatusSummaryItem = ({
  statusSummary,
  readOnly = false,
}: IncidentStatusSummaryItemProps) => {
  const form = useForm({
    mode: 'onChange',
    defaultValues: getDefaultValues(statusSummary),
  });

  return (
    <div className="planning-widget p-3">
      <FormProvider {...form}>
        <div className="d-flex flex-column justify-content-between flex-grow-1">
          <IncidentStatusSummaryFormComponents
            statusSummaryId={statusSummary?.id || ''}
            originalIncidentStatusSummary={getDefaultValues(statusSummary)}
            readOnly={readOnly}
          />
        </div>
      </FormProvider>
    </div>
  );
};

export default IncidentStatusSummaryItem;
